import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { apiConfig } from '../auth/authConfig'
import Breadcrumb from '../components/Breadcrumb'
import KPICard from '../components/KPICard'
import {
    LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer
} from 'recharts'

function formatDollars(val) {
    if (val == null) return '$0'
    const abs = Math.abs(val)
    if (abs >= 1_000_000) return `${val < 0 ? '-' : ''}$${(abs / 1_000_000).toFixed(1)}M`
    if (abs >= 1_000) return `${val < 0 ? '-' : ''}$${(abs / 1_000).toFixed(0)}K`
    return `$${val.toLocaleString()}`
}

function RevenueDetail() {
    const { theme } = useTheme()
    const [searchParams] = useSearchParams()
    const yearFromUrl = searchParams.get('year')
    const [selectedYear, setSelectedYear] = useState(yearFromUrl ? parseInt(yearFromUrl) : null)
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [viewMode, setViewMode] = useState('gross') // 'gross' or 'net'

    useEffect(() => {
        fetchData()
    }, [selectedYear])

    const fetchData = async () => {
        setLoading(true)
        try {
            const qs = selectedYear ? `?year=${selectedYear}` : ''
            const res = await fetch(`${apiConfig.baseUrl}/financials/revenue${qs}`)
            if (!res.ok) throw new Error('Failed to fetch revenue data')
            setData(await res.json())
        } catch (err) {
            setError(err.message)
        } finally {
            setLoading(false)
        }
    }

    const handleYearChange = (yr) => {
        setSelectedYear(yr === selectedYear ? null : yr)
    }

    const displayYear = data?.periodLabel || data?.year || selectedYear || new Date().getFullYear()
    const hasTargets = (data?.ytdGrossTarget || 0) > 0
    const isDark = theme === 'dark'
    const availableYears = data?.availableYears || []

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviator-600"></div>
            </div>
        )
    }

    // Prepare chart data - only months with actual data
    const chartData = (data?.monthly || []).map(m => ({
        ...m,
        name: m.shortLabel || m.month
    })).filter(m => m.grossActual > 0 || m.actual > 0 || m.nriActual > 0)

    const chartColors = {
        gross: '#22c55e',
        net: '#3b82f6',
        grid: isDark ? '#334155' : '#e2e8f0',
        text: isDark ? '#94a3b8' : '#64748b',
        tooltipBg: isDark ? '#1e293b' : '#ffffff',
        tooltipBorder: isDark ? '#334155' : '#e2e8f0'
    }

    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload?.length) return null
        return (
            <div style={{
                background: chartColors.tooltipBg,
                border: `1px solid ${chartColors.tooltipBorder}`,
                borderRadius: 8, padding: '10px 14px', fontSize: 13
            }}>
                <p style={{ fontWeight: 600, marginBottom: 4, color: isDark ? '#fff' : '#111' }}>{label}</p>
                {payload.map((entry, i) => (
                    <p key={i} style={{ color: entry.color, margin: '2px 0' }}>
                        {entry.name}: ${Math.round(entry.value).toLocaleString()}
                    </p>
                ))}
            </div>
        )
    }

    // Revenue breakdown - gross components are positive, deductions are negative
    const breakdown = data?.revenueBreakdown || []
    const grossItems = breakdown.filter(r => r.amount > 0)
    const deductionItems = breakdown.filter(r => r.amount <= 0)

    return (
        <div className="space-y-6">
            <Breadcrumb items={[
                { label: 'Financials', path: '/financials' },
                { label: 'Revenue' }
            ]} />

            <div className={`border-b-2 ${isDark ? 'border-blue-800' : 'border-blue-200'} pb-4`}>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                        <h1 className={`text-xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} flex items-center gap-2 sm:gap-3`}>
                            <span className="inline-block w-1 h-6 sm:h-8 bg-blue-500 flex-shrink-0"></span>
                            Revenue
                        </h1>
                        <p className={`${isDark ? 'text-blue-300' : 'text-blue-700'} text-xs sm:text-sm mt-1 ml-3 sm:ml-4`}>
                            {data?.isRolling ? `Rolling 13 months` : displayYear} {'\u2022'} Data through {data?.periodLabel || displayYear}
                            {loading && <span className="ml-2 text-xs opacity-60">Updating...</span>}
                        </p>
                    </div>
                    <div className="flex items-center gap-3 ml-3 sm:ml-0">
                        {/* Gross/Net Toggle */}
                        <div className={`inline-flex rounded-lg p-0.5 ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-gray-200 border border-gray-300'}`}>
                            <button onClick={() => setViewMode('gross')}
                                className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-all ${
                                    viewMode === 'gross' ? 'bg-blue-600 text-white shadow-sm'
                                        : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                                }`}>Gross</button>
                            <button onClick={() => setViewMode('net')}
                                className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-all ${
                                    viewMode === 'net' ? 'bg-blue-600 text-white shadow-sm'
                                        : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'
                                }`}>Net (NRI)</button>
                        </div>
                        {/* Year Toggle Pills */}
                        <div className="flex gap-1">
                            <button onClick={() => setSelectedYear(null)}
                                className={`px-3 py-1.5 text-sm font-medium rounded-full transition-all ${
                                    selectedYear === null
                                        ? 'bg-blue-600 text-white shadow-lg'
                                        : isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}>13 Mo</button>
                            {availableYears.map(yr => (
                                <button key={yr} onClick={() => handleYearChange(yr)}
                                    className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all ${
                                        yr === selectedYear
                                            ? 'bg-blue-600 text-white shadow-lg'
                                            : isDark ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}>{yr}</button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {error && (
                <div className="bg-red-900/20 border border-red-800 rounded-lg p-4 text-red-400">
                    <p>{error}</p>
                </div>
            )}

            {data && (() => {
                const isNet = viewMode === 'net'
                const topLineValue = isNet ? (data.ytdNri || 0) : (data.ytdGross || 0)
                const topLineTarget = isNet ? (data.ytdNetTarget || 0) : (data.ytdGrossTarget || 0)
                return (
                <>
                    {/* KPI Cards */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                        <KPICard title={isNet ? "NRI Revenue" : "Gross Revenue"} value={`$${Math.round(topLineValue).toLocaleString()}`}
                            subtitle={hasTargets ? `Target: $${Math.round(topLineTarget).toLocaleString()}` : 'Period Total'} />
                        <KPICard title="Net Revenue" value={`$${Math.round(data.ytdActual || 0).toLocaleString()}`}
                            subtitle={hasTargets ? `Target: $${Math.round(data.ytdNetTarget).toLocaleString()}` : 'Period Total'} />
                        {hasTargets && (
                            <KPICard title="vs Target"
                                value={topLineTarget ? `${((topLineValue / topLineTarget) * 100).toFixed(0)}` : '--'}
                                unit="%"
                                trend={topLineValue >= topLineTarget ? 'up' : 'down'} />
                        )}
                        <KPICard title="Net / Gross"
                            value={data.ytdGross ? `${((data.ytdActual / data.ytdGross) * 100).toFixed(1)}` : '--'}
                            unit="%"
                            subtitle="After deductions" />
                    </div>

                    {/* Revenue Trend Line Chart */}
                    {chartData.length > 0 && (
                        <div className="chart-container">
                            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>
                                Monthly {isNet ? 'NRI' : 'Gross'} Revenue Trend
                            </h2>
                            <ResponsiveContainer width="100%" height={300}>
                                <LineChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                    <XAxis dataKey="name" tick={{ fill: chartColors.text, fontSize: 12 }} />
                                    <YAxis tickFormatter={formatDollars} tick={{ fill: chartColors.text, fontSize: 12 }} width={65} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend wrapperStyle={{ fontSize: 13, color: chartColors.text }} />
                                    <Line type="monotone" dataKey={isNet ? 'nriActual' : 'grossActual'} name={isNet ? 'NRI Revenue' : 'Gross Revenue'}
                                        stroke={chartColors.gross} strokeWidth={2} dot={{ r: 4 }} />
                                    <Line type="monotone" dataKey="actual" name="Net Revenue"
                                        stroke={chartColors.net} strokeWidth={2} dot={{ r: 4 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* Revenue by Type - Stacked Bar */}
                    {chartData.length > 0 && (
                        <div className="chart-container">
                            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>
                                {isNet ? 'NRI' : 'Gross'} Revenue by Type
                            </h2>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={chartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                                    <XAxis dataKey="name" tick={{ fill: chartColors.text, fontSize: 12 }} />
                                    <YAxis tickFormatter={formatDollars} tick={{ fill: chartColors.text, fontSize: 12 }} width={65} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend wrapperStyle={{ fontSize: 13, color: chartColors.text }} />
                                    <Bar dataKey={isNet ? 'nriOilSales' : 'oilSales'} name="Oil Sales" stackId="rev" fill="#22c55e" />
                                    <Bar dataKey={isNet ? 'nriGasSales' : 'gasSales'} name="Gas Sales" stackId="rev" fill="#f97316" />
                                    <Bar dataKey={isNet ? 'nriPlantSales' : 'plantSales'} name="Plant / NGL" stackId="rev" fill="#a855f7" radius={[2, 2, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    )}

                    {/* Revenue Breakdown Table */}
                    {breakdown.length > 0 && (() => {
                        const nriBreakdown = data?.nriRevenueBreakdown || []
                        const displayBreakdown = isNet ? nriBreakdown : breakdown
                        const displayGrossItems = displayBreakdown.filter(r => r.amount > 0)
                        const displayTotal = isNet ? (data.ytdNri || 0) : (data.ytdGross || 0)
                        return (
                        <div className="chart-container">
                            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>
                                {isNet ? 'NRI' : 'Gross'} Revenue Breakdown ({displayYear})
                            </h2>
                            <div className="overflow-x-auto">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Category</th>
                                            <th className="text-right">Amount</th>
                                            <th className="text-right">% of {isNet ? 'NRI' : 'Gross'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayGrossItems.map((row, i) => {
                                            const pct = displayTotal ? ((row.amount / displayTotal) * 100).toFixed(1) : '--'
                                            return (
                                                <tr key={i}>
                                                    <td className="font-medium">{row.type}</td>
                                                    <td className="text-right text-green-400">${Math.round(row.amount).toLocaleString()}</td>
                                                    <td className="text-right">{pct}%</td>
                                                </tr>
                                            )
                                        })}
                                        <tr className={`font-semibold ${isDark ? 'border-t border-slate-600' : 'border-t border-gray-300'}`}>
                                            <td>{isNet ? 'NRI Revenue' : 'Gross Revenue'}</td>
                                            <td className="text-right">${Math.round(displayTotal).toLocaleString()}</td>
                                            <td className="text-right">100%</td>
                                        </tr>
                                        {!isNet && deductionItems.map((row, i) => {
                                            const pct = data.ytdGross ? ((row.amount / data.ytdGross) * 100).toFixed(1) : '--'
                                            return (
                                                <tr key={`d-${i}`}>
                                                    <td className="font-medium">{row.type}</td>
                                                    <td className="text-right text-red-400">(${Math.round(Math.abs(row.amount)).toLocaleString()})</td>
                                                    <td className="text-right">{pct}%</td>
                                                </tr>
                                            )
                                        })}
                                        {!isNet && (
                                        <tr className={`font-bold ${isDark ? 'border-t-2 border-slate-500' : 'border-t-2 border-gray-400'}`}>
                                            <td>Net Revenue</td>
                                            <td className="text-right">${Math.round(data.ytdActual || 0).toLocaleString()}</td>
                                            <td className="text-right">{data.ytdGross ? ((data.ytdActual / data.ytdGross) * 100).toFixed(1) : '--'}%</td>
                                        </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        )
                    })()}

                    {/* Monthly Table */}
                    {data.monthly && data.monthly.length > 0 && (
                        <div className="chart-container">
                            <h2 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>
                                Monthly {isNet ? 'NRI' : ''} Revenue
                            </h2>
                            <div className="overflow-x-auto">
                                <table className="data-table">
                                    <thead>
                                        <tr>
                                            <th>Month</th>
                                            <th className="text-right">Oil Sales</th>
                                            <th className="text-right">Gas Sales</th>
                                            <th className="text-right">Plant/NGL</th>
                                            <th className="text-right">{isNet ? 'NRI' : 'Gross'}</th>
                                            {!isNet && <th className="text-right">Sev Tax</th>}
                                            {!isNet && <th className="text-right">Royalties</th>}
                                            {!isNet && <th className="text-right">Other Ded.</th>}
                                            {!isNet && <th className="text-right">Net</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.monthly.map((row, i) => {
                                            const hasActual = (row.grossActual || 0) > 0
                                            const oil = isNet ? (row.nriOilSales || 0) : (row.oilSales || 0)
                                            const gas = isNet ? (row.nriGasSales || 0) : (row.gasSales || 0)
                                            const plant = isNet ? (row.nriPlantSales || 0) : (row.plantSales || 0)
                                            const total = isNet ? (row.nriActual || 0) : (row.grossActual || 0)
                                            return (
                                                <tr key={i}>
                                                    <td className="font-medium">{row.month}</td>
                                                    <td className="text-right">{hasActual ? `$${Math.round(oil).toLocaleString()}` : '--'}</td>
                                                    <td className="text-right">{hasActual ? `$${Math.round(gas).toLocaleString()}` : '--'}</td>
                                                    <td className="text-right">{hasActual ? `$${Math.round(plant).toLocaleString()}` : '--'}</td>
                                                    <td className="text-right font-medium">{hasActual ? `$${Math.round(total).toLocaleString()}` : '--'}</td>
                                                    {!isNet && <td className="text-right text-red-400">{hasActual ? `(${Math.round(Math.abs(row.severanceTax)).toLocaleString()})` : '--'}</td>}
                                                    {!isNet && <td className="text-right text-red-400">{hasActual ? `(${Math.round(Math.abs(row.royalties)).toLocaleString()})` : '--'}</td>}
                                                    {!isNet && <td className="text-right text-red-400">{hasActual ? `(${Math.round(Math.abs(row.otherDeductions)).toLocaleString()})` : '--'}</td>}
                                                    {!isNet && <td className="text-right font-medium">{hasActual ? `$${Math.round(row.actual).toLocaleString()}` : '--'}</td>}
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </>
                )
            })()}
        </div>
    )
}

export default RevenueDetail
